import React, { lazy, Suspense } from 'react'
import { observable } from 'mobx'
import { observer } from 'mobx-react'
import { Button, Row } from 'react-bootstrap'
import { IS_BROWSER } from 'config/appConfig'
import {
  getAddressFieldsJsonBasedOnCountry,
  getAddressOnlyFieldsForValidation,
  checkIfTwoFormValuesAreEqual,
} from 'src/deps'
import {
  storeContainer,
  addressContainer,
  accountsAddressContainer,
  customerContainer,
  accountsContainer,
  cartContainer,
  checkoutContainer,
} from 'src/models'
import { i18nTranslate, trackEnhancedCheckout } from 'src/utils'
import { AddressValidation, toastState } from 'src/views/components'
import {
  removeShippingAddressFormValidationMeta,
  SHIPPING_ADDRESS_FORM_VALIDATED_FIELD,
} from 'src/views/components/CommonFormForCS/shippingAddressFormValidation'
import './styles.scss'

@observer
class LegalAddressAddNewForm extends React.Component {
  @observable isToDisableAddNewContinueButton = false
  @observable isToShowAddressSuggestionModal = false
  @observable isEnteredAddressInvalid = false
  @observable recommendationAddresses = []

  currentMarket = storeContainer?.defaultShippingRegion
    ? 'Address' + storeContainer?.defaultShippingRegion
    : 'AddressUS'
  AddressForm = lazy(
    () =>
      import(
        'src/views/addresses/' + this.currentMarket + '/' + this.currentMarket
      )
  )

  constructor(props) {
    super(props)
  }

  getFormFieldsBasedOnCountry = async () => {
    let fieldJson = await getAddressFieldsJsonBasedOnCountry(
      storeContainer.defaultShippingRegion
    )
    let formFields = await getAddressOnlyFieldsForValidation(
      fieldJson,
      'legalAddress'
    )
    return formFields
  }

  handleAddressSuggestionModalClose = () => {
    this.isToShowAddressSuggestionModal = false
    this.isToDisableAddNewContinueButton = false
  }

  updateAccountLegalAddress = async formData => {
    const accountAddress =
      accountsContainer?.profileResponse?.addresses?.[0] || {}
    const { accountId = '', addressId = '' } = accountAddress || {}
    const {
      firstName = '',
      lastName = '',
      email = '',
      phone = '',
      phoneNumber = '',
    } = customerContainer?.profileResponse || {}
    const accountIdFromProfile = customerContainer.accountId || ''

    const formDataTemp = {
      ...removeShippingAddressFormValidationMeta(formData),
      firstName,
      lastName,
      email,
      phone: phone || phoneNumber,
      accountId: accountId || accountIdFromProfile || '',
      addressId,
    }

    let updateAddressResponse
    if (Object.keys(accountAddress)?.length > 0) {
      updateAddressResponse =
        await accountsAddressContainer.updateAccountAddress(formDataTemp, false)
    } else {
      updateAddressResponse =
        await accountsAddressContainer.addAddress(formDataTemp)
    }
    if (accountsAddressContainer.isSuccessResponse(updateAddressResponse)) {
      toastState.setToastMessage(
        i18nTranslate(
          'legalAddress.savedNewAddress',
          'Your address has been updated.'
        ),
        true
      )
      await accountsContainer.getProfile(customerContainer?.accountId)
      this.isToDisableAddNewContinueButton = false
      checkoutContainer.isLegalAddressAdded = true
      this.props.handleNextAccordionContinue()
    } else {
      this.isToDisableAddNewContinueButton = false
      toastState.setToastMessage(
        i18nTranslate(
          'legalAddress.pleaseSelectDifferent',
          'Please try a different address.'
        )
      )
    }
  }

  handleAddressSuggestionModalContinue = async formData => {
    if (formData?.selectedIndex <= 0) {
      let validateOriginalAddressResponse =
        await addressContainer.originalAddressApiCall(formData)
      this.handleAddressSuggestionModalClose()
      if (addressContainer.isSuccessResponse(validateOriginalAddressResponse)) {
        await this.updateAccountLegalAddress(formData)
      } else {
        this.isToDisableAddNewContinueButton = false
        if (
          validateOriginalAddressResponse?.message === 'input.validation.failed'
        ) {
          toastState.setToastMessage(
            i18nTranslate(
              'legalAddress.pleaseCorrectTheAddress',
              'Please correct the address to proceed.'
            )
          )
        } else if (
          validateOriginalAddressResponse?.code ===
          'ONESOURCE_ADPLGIN_VAL_ERROR'
        ) {
          toastState.setToastMessage(
            i18nTranslate(
              'legalAddress.invalidAddress',
              'Address provided is invalid'
            )
          )
        }
      }
    } else {
      this.handleAddressSuggestionModalClose()
      await this.updateAccountLegalAddress(formData)
      await cartContainer.viewCartForTax()
    }
  }

  handleAddNewLegalAddressContinue = async (formData, savedAddress) => {
    this.isToDisableAddNewContinueButton = true
    const { isToMakeValidationApiCall = false } = formData
    const isToMakeAddressValidationApi =
      !formData?.[SHIPPING_ADDRESS_FORM_VALIDATED_FIELD] &&
      (storeContainer?.defaultShippingRegion === 'US' ||
        storeContainer?.defaultShippingRegion === 'CA' ||
        isToMakeValidationApiCall)
    let formFields = await this.getFormFieldsBasedOnCountry()
    const products = cartContainer?.cartResponse?.items || []
    trackEnhancedCheckout({
      type: 'checkout',
      action: {
        step: '2',
        option: i18nTranslate('checkout.legalAddress', 'Legal Address'),
      },
      products: products,
    })
    if (isToMakeAddressValidationApi) {
      const validationResponse =
        await addressContainer.validateAddress(formData)
      if (validationResponse?.code === 'ADCOVAL0003') {
        this.recommendationAddresses = [{ ...formData }, []]
        this.isEnteredAddressInvalid = true
        this.isToShowAddressSuggestionModal = true
      } else if (validationResponse?.code == 'ONESOURCE_ADPLGIN_VAL_ERROR') {
        this.isToShowAddressSuggestionModal = false
        toastState.setToastMessage(
          i18nTranslate('legalAddress.addressNotAdded', 'Address not added')
        )
        this.isToDisableAddNewContinueButton = false
        return
      } else if (addressContainer.isSuccessResponse(validationResponse)) {
        const recommendationAddress =
          validationResponse?.address?.validationDetails?.[0]
            ?.recommendations || []
        let firstAddress = recommendationAddress?.[0] || {}
        let isFirstSuggestedAddressSameAsCurrentAddress =
          await checkIfTwoFormValuesAreEqual(formFields, formData, firstAddress)

        if (
          Object.keys(firstAddress)?.length > 0 &&
          !isFirstSuggestedAddressSameAsCurrentAddress
        ) {
          this.recommendationAddresses = [
            { ...formData },
            ...recommendationAddress,
          ]
          this.isEnteredAddressInvalid = false
          this.isToShowAddressSuggestionModal = true
        } else {
          this.handleAddressSuggestionModalContinue(formData)
        }
      } else {
        this.isToShowAddressSuggestionModal = false
        toastState.setToastMessage(
          i18nTranslate('legalAddress.addressNotAdded', 'Address not added')
        )
        return
      }
    } else {
      await this.handleAddressSuggestionModalContinue(formData)
    }
  }

  renderAddressBookSubmitButton = () => {
    return (
      <Row
        noGutters
        className="legal-address-add-address-button justify-content-end">
        <Button
          className="legal-address-continue-button mt-3 px-5 py-2"
          variant="primary"
          type="submit"
          disabled={this.isToDisableAddNewContinueButton}
          data-testid="qa-legal-address-continue">
          {i18nTranslate('legalAddress.addNewAddressContinue', 'Continue')}
        </Button>
      </Row>
    )
  }

  render() {
    const accountAddress =
      accountsContainer?.profileResponse?.addresses?.[0] || {}

    if (IS_BROWSER) {
      return (
        <>
          <Suspense fallback={<div>Loading...</div>}>
            <this.AddressForm
              onSubmit={this.handleAddNewLegalAddressContinue}
              renderSubmitButton={this.renderAddressBookSubmitButton}
              type="legalAddress"
              className="legal-address-add-new-form"
              addressInfo={accountAddress}
              isToHideLegalAddressRadioOptions={true}
            />
          </Suspense>
          {this.isToShowAddressSuggestionModal && (
            <AddressValidation
              open={this.isToShowAddressSuggestionModal}
              addressInvalidFlag={this.isEnteredAddressInvalid}
              recommendations={this.recommendationAddresses}
              handleClose={this.handleAddressSuggestionModalClose}
              handleClickOnPro={this.handleAddressSuggestionModalContinue}
            />
          )}
        </>
      )
    }

    return <></>
  }
}

export default LegalAddressAddNewForm
export { LegalAddressAddNewForm }
